
import { Button } from "@/components/ui/button";
import { ScriptureResponse } from "@/hooks/useFindScripture";
import { Share2, Mail, MessageSquare, Facebook } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { updateAllMetadata } from "@/utils/dynamicMetadata";
import { toast } from "@/hooks/use-toast";
import { refreshMetadata } from "@/utils/metadataRefresher";
import { shareViaEmail, shareViaSMS, shareToFacebook } from "@/utils/socialShareUtils";

interface ShareButtonsProps {
  scripture: ScriptureResponse;
  type: 'verse' | 'commentary' | 'guidance';
}

const ShareButtons = ({ scripture, type }: ShareButtonsProps) => {
  // Update meta tags before opening dropdown
  const handleTriggerClick = () => {
    // Update metadata to reflect what's being shared
    updateAllMetadata({
      title: `Selah | ${scripture.reference}`,
      description: scripture.text,
      type: "article",
    });
    
    // Force refresh of metadata
    refreshMetadata();
  };

  // Helper functions for sharing
  const handleShareViaEmail = () => {
    const subject = `Selah: ${scripture.reference}`;
    let body = `I wanted to share this scripture with you:\n\n${scripture.reference}\n${scripture.text}\n\n`;
    
    if (type === 'commentary') {
      body += `Commentary: ${scripture.commentary}\n\n`;
    } else if (type === 'guidance') {
      body += `Guidance: ${scripture.guidance}\n\n`;
    }
    
    body += "Shared from Selah - Your Digital Bible Companion";
    
    shareViaEmail(subject, body);
  };

  const handleShareViaSMS = () => {
    let text = `${scripture.reference} - ${scripture.text}`;
    
    if (type === 'commentary' && scripture.commentary) {
      text += `\n\nCommentary: ${scripture.commentary}`;
    } else if (type === 'guidance' && scripture.guidance) {
      text += `\n\nGuidance: ${scripture.guidance}`;
    }
    
    text += "\n\nShared from Selah";
    
    shareViaSMS(text);
  };

  const handleShareToFacebook = () => {
    // First update metadata to reflect what's being shared
    updateAllMetadata({
      title: `Selah | ${scripture.reference}`,
      description: scripture.text,
      type: "article",
    });
    
    // Add a short delay to ensure metadata is updated
    setTimeout(() => {
      shareToFacebook();
    }, 300);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="blue" 
          size="sm" 
          className="flex items-center gap-1" 
          data-share-button="true" 
          onClick={handleTriggerClick}
        >
          <Share2 className="h-4 w-4" />
          <span>Share</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent 
        align="end" 
        className="bg-white border border-gray-100 shadow-lg"
      >
        <DropdownMenuLabel>Share {type}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleShareViaEmail} className="cursor-pointer">
          <Mail className="h-4 w-4 mr-2" />
          <span>Email</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleShareViaSMS} className="cursor-pointer">
          <MessageSquare className="h-4 w-4 mr-2" />
          <span>Text Message</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleShareToFacebook} className="cursor-pointer">
          <Facebook className="h-4 w-4 mr-2" />
          <span>Facebook</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ShareButtons;
